/**
 * Short helper text for contribution category cards when the label alone is unclear.
 *
 * Keys are `categoryId` or `featureType:categoryId` when the same id means different things
 * (e.g. `landuse:residential` vs `building:residential`).
 *
 * Road types keep inline hints on ROAD_CATEGORIES in AddLocationPanel (policy doc §9).
 * applyCategoryHints() fills any category without an inline hint from this map.
 */

export const CONTRIBUTION_CATEGORY_HINTS: Record<string, string> = {
  // — Place —
  city: 'Major city (e.g. Accra, Kumasi, Tamale)',
  town: 'Recognised town with a centre and services (e.g. Adjen Kotoku)',
  village: 'Smaller rural settlement — bigger than a hamlet',
  hamlet: 'Tiny cluster of homes, no real town centre',
  suburb: 'Named part of a city (e.g. Osu, Madina, East Legon)',
  neighbourhood: 'Small named area inside a town or suburb',
  quarter: 'Ward or district within a larger town',
  locality: 'Named place when unsure — prefer Town or Village if you can',

  // — Transport stops —
  bus_station: 'Main station — many routes start or end here',
  bus_stop: 'Roadside stop where buses pick up passengers',
  trotro: 'Shared minibus stop — the common Ghana public transport',
  taxi: 'Taxi rank where drivers wait for passengers',
  ferry_terminal: 'Passenger ferry boarding point',
  rail_station: 'Train station or rail halt',

  // — POI (API + fallback) —
  restaurant: 'Sit-down restaurant or eatery with table service',
  cafe: 'Coffee shop or casual café',
  bar: 'Bar or pub serving drinks',
  bakery: 'Bread, pastry, or baked-goods shop',
  supermarket: 'Large grocery supermarket',
  bank: 'Bank branch or main office — not an outdoor ATM',
  atm: 'Cash machine — usually outside a bank or shop',
  fuel: 'Petrol or diesel filling station',
  parking: 'Paid or public car park as a POI point',
  hotel: 'Hotel or large lodging property',
  motel: 'Roadside motel or motor lodge',
  hostel: 'Budget hostel or shared lodging',
  mall: 'Shopping mall or large retail centre',
  police: 'Police station or post',
  post_office: 'Post office branch',
  townhall: 'Town hall or municipal office building',
  school: 'Primary or secondary school',
  university: 'University or college campus main site',
  library: 'Public or community library',
  hospital: 'Hospital or major medical facility',
  clinic: 'Outpatient clinic — smaller than a hospital',
  pharmacy: 'Chemist or pharmacy shop',
  dentist: 'Dental clinic or surgery',
  stadium: 'Sports stadium or arena',
  beach_resort: 'Beach hotel or resort',
  park: 'Named public park as a POI point (use Land Use for the whole zone)',
  hairdresser: 'Hair salon or barber shop',
  company: 'Company office when no finer category fits',
  government: 'Government ministry or public office',
  bureau_de_change: 'Licensed forex / currency exchange booth',
  mobile_money_agent: 'MTN MoMo, Telecel Cash, or AirtelTigo agent point',
  place_of_worship: 'Church, mosque, temple, or shrine open to visitors',
  community_centre: 'Hall or centre for local meetings and events',
  convenience: 'Small corner shop — bigger than a kiosk',
  fast_food: 'Takeaway or quick-service food (not a sit-down restaurant)',
  guest_house: 'Small lodging — simpler than a full hotel',
  marketplace: 'Open-air market or trading area',
  sports_centre: 'Indoor/outdoor sports facility (not a single pitch)',
  pub: 'Pub or public house serving drinks',
  food_court: 'Shared indoor food court with multiple vendors',
  ice_cream: 'Ice cream shop or frozen dessert stand',
  money_transfer: 'Money transfer or remittance service',
  doctor: 'Doctor\'s office or GP surgery',
  veterinary: 'Veterinary clinic or animal hospital',
  kindergarten: 'Nursery or pre-school',
  car_rental: 'Car hire office or rental lot',
  car_repair: 'Mechanic, garage, or vehicle repair shop',
  clothes: 'Clothing or fashion retail shop',
  electronics: 'Electronics or phone shop',
  furniture: 'Furniture or home-goods store',
  courthouse: 'Court building or judicial centre',
  fire_station: 'Fire brigade station',
  embassy: 'Embassy or high commission',
  shop: 'General retail shop when no finer type fits',
  laundry: 'Laundry or dry-cleaning service',
  cinema: 'Cinema or movie theatre',
  nightclub: 'Nightclub or late-night venue',
  pitch: 'Single football or sports field',
  college: 'Secondary or tertiary college (not a university)',
  social_facility: 'Charity, NGO, or social welfare centre',
  microfinance: 'Susu, microcredit, or small-loan office',
  studio: 'Photo, recording, or creative studio',
  general: 'Business or place when no other category fits',

  // — Buildings —
  'building:residential': 'Single home, compound, or small residential block',
  'building:apartments': 'Block of flats or multi-unit housing',
  'building:commercial': 'Shop, restaurant, or mixed-use commercial building',
  'building:industrial': 'Factory, workshop, or industrial building',
  'building:school': 'School building — pin or trace the main block',
  'building:hospital': 'Hospital or clinic building footprint',
  'building:church': 'Church or chapel building',
  'building:mosque': 'Mosque building',
  'building:hotel': 'Hotel or hostel building footprint',
  'building:office': 'Office block or business premises',
  'building:retail': 'Standalone shop or retail unit',
  'building:warehouse': 'Storage warehouse or depot',
  'building:government': 'Government or municipal building',
  'building:farm': 'Barn, shed, or farm structure (not the farmland itself)',
  'building:garage': 'Standalone garage or car shelter',
  'building:construction': 'Building shell or site still under construction',

  // — Land use (zoning polygons) —
  'landuse:residential': 'Area mainly used for homes — draw the zone boundary',
  'landuse:commercial': 'Offices, services, and mixed business activity',
  'landuse:retail': 'Shopping-focused zone — markets, malls, shop rows',
  'landuse:industrial': 'Factories, workshops, and industrial estates',
  'landuse:apartment': 'Apartment block or flats landuse zone',
  'landuse:school': 'School grounds as a landuse polygon',
  'landuse:place_of_worship': 'Church, mosque, or temple grounds',
  'landuse:hospital': 'Hospital campus or medical grounds',
  'landuse:airport': 'Airport landuse boundary (not the runway lines)',
  'landuse:museum': 'Museum or heritage site grounds',
  'landuse:nature_reserve': 'Protected nature reserve boundary',
  'landuse:refugee_site': 'Refugee camp or displacement settlement area',
  'landuse:farm': 'General farm landuse area',
  'landuse:park': 'Park or public green landuse zone',
  'landuse:stadium': 'Stadium or sports ground landuse area',
  'landuse:pitch': 'Sports pitch landuse zone',
  'landuse:garden': 'Formal garden or ornamental grounds',
  'landuse:marketplace': 'Open-air market landuse zone',
  'landuse:bus_station': 'Bus station forecourt or terminal area',
  'landuse:parking': 'Parking area as landuse (not a POI point)',
  'landuse:fuel': 'Fuel station forecourt landuse area',
  'landuse:car_repair': 'Vehicle repair yard or garage area',
  'landuse:hotel': 'Hotel grounds or resort landuse zone',
  'landuse:hostel': 'Hostel or lodging grounds',
  'landuse:rock': 'Rocky or exposed stone landuse patch',
  'landuse:sand': 'Sand extraction or sandy landuse area',
  'landuse:dump': 'Rubbish dump or waste pile area',
  'landuse:education': 'School or university grounds',
  'landuse:religious': 'Church, mosque, or temple grounds',
  'landuse:cemetery': 'Burial ground or cemetery boundary',
  'landuse:government': 'Ministry, court, or state grounds',
  'landuse:public_office': 'Government or municipal office grounds',
  'landuse:military': 'Barracks, base, or restricted military land',
  'landuse:farmland': 'Open farmland or cropland zone',
  'landuse:farmyard': 'Farm buildings and yard around them',
  'landuse:orchard': 'Fruit or nut orchard plantation',
  'landuse:cultivation': 'Actively farmed plots within a larger area',
  'landuse:greenhouse_horticulture': 'Greenhouses or commercial horticulture',
  'landuse:plant_nursery': 'Commercial plant nursery grounds',
  'landuse:aquaculture': 'Fish ponds or aquaculture site',
  'landuse:animal_keeping': 'Livestock pens, kraal, or grazing land',
  'landuse:allotments': 'Shared community garden plots',
  'landuse:forest': 'Managed or planted forest landuse area',
  'landuse:grass': 'Grass lawn, park lawn, or open grass zone',
  'landuse:meadow': 'Natural or semi-natural meadow',
  'landuse:recreation_ground': 'Sports field or recreation landuse area',
  'landuse:village_green': 'Shared green at the heart of a village',
  'landuse:scrub': 'Managed scrub landuse area',
  'landuse:flowerbed': 'Formal flowerbed or ornamental garden bed',
  'landuse:construction': 'Development site under active construction',
  'landuse:brownfield': 'Former industrial or derelict site',
  'landuse:greenfield': 'Undeveloped plot',
  'landuse:railway': 'Rail corridor landuse (the zone, not the tracks)',
  'landuse:harbour': 'Port or harbour landuse zone',
  'landuse:reservoir': 'Reservoir landuse boundary',
  'landuse:basin': 'Retention or storage basin',
  'landuse:car_park': 'Parking lot as a mapped landuse area — name shows on map (e.g. Visitor Car Park)',
  'landuse:garages': 'Garage blocks or structured parking zone',
  'landuse:quarry': 'Active or disused quarry / mining pit',
  'landuse:landfill': 'Rubbish dump or landfill site',
  'landuse:logging': 'Commercial logging or forestry operation area',
  'landuse:salt_pond': 'Coastal salt evaporation ponds',

  // — Land cover (natural surface — not zoning) —
  'landcover:wood': 'Natural woodland patch (land cover, not a farm zone)',
  'landcover:grassland': 'Open grassland surface (natural land cover)',
  'landcover:scrub': 'Low bush or scrubland surface',
  'landcover:shrub': 'Dense low woody vegetation',
  'landcover:heath': 'Open heath or shrubby grassland',
  'landcover:wetland': 'Marsh, swamp, or boggy ground',
  'landcover:water': 'Standing water surface (pond edge, lake, etc.)',
  'landcover:tree_row': 'Line of trees along a road or field edge',
  'landcover:sand': 'Sandy beach, dune, or bare sand patch',
  'landcover:mud': 'Mudflat or tidal mud surface',
  'landcover:beach': 'Sandy or pebble beach shoreline',
  'landcover:shingle': 'Pebble or shingle shore',
  'landcover:bare_rock': 'Exposed rock with little vegetation',
  'landcover:cliff': 'Steep rock face or escarpment edge',
  'landcover:ridge': 'Long narrow hill crest or ridge line',
  'landcover:peak': 'Summit or hilltop point feature',
  'landcover:mountain_range': 'Named mountain range extent',
  'landcover:arete': 'Sharp narrow mountain ridge',
  'landcover:land': 'Peninsula, cape, or named land projection',

  // — Structures —
  'structure:wall': 'Boundary or compound wall (line or polygon)',
  'structure:fence': 'Wire, wooden, or metal fence line',
  'structure:gate': 'Vehicle or pedestrian gate in a wall or fence',
  'structure:lift_gate': 'Boom barrier that lifts for vehicles',
  'structure:hedge': 'Living hedge boundary',
  'structure:retaining_wall': 'Wall holding back soil or a slope',
  'structure:city_wall': 'Historic or defensive city wall segment',
  'structure:bollard': 'Post blocking vehicle access',
  'structure:guard_rail': 'Safety rail along a road edge or bridge',
  'structure:ditch': 'Drainage ditch or channel (dry or seasonal)',
  'structure:block': 'Concrete barrier or road block',
  'structure:entrance': 'Gateway or formal entry through a wall/fence',
  'structure:water_well': 'Covered well or borehole access point',
  'structure:utility_pole': 'Street pole with both a light and a speed/traffic camera',

  // — Traffic control —
  camera_traffic: 'Combined pole: traffic signal + enforcement camera (+ light). Use label e.g. SL-01',
  crossing_signals: 'Pedestrian crossing signal only (pelican / puffin head)',
  crossing_traffic: 'Combined pole: vehicle traffic light + pedestrian crossing signal',
  street_lamp: 'Pole-mounted street light along a road',
  speed_camera: 'Fixed speed or traffic enforcement camera',
  traffic_signals: 'Vehicle traffic signal — use label e.g. Junction A',

  // — Street furniture POIs —
  bench: 'Public bench — label optional; map shows Bench if unnamed',
  waste_basket: 'Litter / waste bin — label optional; map shows Litter Bin if unnamed',

  // — Pedestrian paths (road trace) —
  'road:footway': 'Trace the footway — name optional; map shows Footway if unnamed',
  'road:path': 'Trace the path — name optional; map shows Path if unnamed',
  'road:cycleway': 'Trace the cycleway — name optional; map shows Cycleway if unnamed',
  'road:shoulder': 'Trace the shoulder centreline — name optional; map shows Shoulder if unnamed',
  'road:parking_lane': 'Trace the parking lane centreline — name optional; map shows Parking lane if unnamed',
  'road:pedestrian': 'Car-free street or plaza — walkers only',
  'road:steps': 'Outdoor stairs or stepped path between levels',
  'road:pedestrian_crossing': 'Zebra / marked crossing — sets crossing_type=zebra for road-zebra* stripes',
  'road:construction': 'Road being built or closed — trace planned alignment if visible',

  // — Aeroway —
  'aeroway:aerodrome': 'Airport or airfield boundary',
  'aeroway:runway': 'Paved runway centreline or runway polygon',
  'aeroway:taxiway': 'Aircraft movement lane between runway and apron',
  'aeroway:apron': 'Paved area where aircraft park near terminals',
  'aeroway:helipad': 'Helicopter landing pad (H marking)',
  'aeroway:terminal': 'Air terminal building footprint',
  'aeroway:hangar': 'Aircraft hangar or maintenance building',
  'aeroway:jet_bridge': 'Passenger boarding bridge to an aircraft',
  'aeroway:parking_position': 'Marked aircraft stand on the apron',
  'aeroway:gate': 'Airport boarding gate (not a street gate)',
  'aeroway:taxiway_centerline': 'Taxiway centreline for aircraft movement',
  'aeroway:holding_position': 'Marked hold point on taxiway or apron',
  'aeroway:stopway': 'Runway stopway or overrun area',
  'aeroway:displaced_threshold': 'Displaced runway threshold marking',
  'aeroway:windsock': 'Windsock or wind direction indicator',
  'aeroway:navigationaid': 'Airfield navigation aid (VOR, NDB, etc.)',

  // — Water bodies (polygons) —
  'water:lake': 'Named lake — trace the water edge',
  'water:water': 'General water body when type is unclear',
  'water:pond': 'Small pond or farm reservoir',
  'water:river': 'River channel as a water polygon — trace the wetted area',
  'water:stream': 'Small stream as a water polygon',
  'water:reservoir': 'Large storage reservoir (water polygon)',
  'water:canal': 'Canal as a water body polygon',
  'water:basin': 'Storage or retention pool (still water)',
  'water:oxbow': 'U-shaped leftover lake from a former river bend',
  'water:wastewater': 'Sewage lagoon or treatment pond',
  'water:lagoon': 'Coastal lagoon separated from the sea',
  'water:lock': 'Canal lock chamber (water area)',
  'water:ditch': 'Drainage pond or ditch holding water',
  'water:harbour': 'Harbour basin as a water polygon',
  'water:dam': 'Dam wall or impoundment structure',

  // — Waterways (linear flow paths) —
  'waterway:river': 'River centreline — trace the flow path',
  'waterway:stream': 'Stream centreline',
  'waterway:canal': 'Canal centreline',
  'waterway:drain': 'Engineered drainage channel',
  'waterway:ditch': 'Small drainage ditch or channel',
  'waterway:waterfall': 'Waterfall location on a stream or river',
  'waterway:lock_gate': 'Canal lock gate structure on a waterway',
  'waterway:weir': 'Low dam or overflow across a stream',
  'waterway:dock': 'Boat mooring or loading point on a waterway',
  'waterway:dam': 'Dam structure spanning a waterway',

  // — Landmarks —
  monument: 'Statue, obelisk, or public monument',
  memorial: 'War memorial or commemorative structure',
  castle: 'Historic castle, fort, or defensive tower',
  aircraft: 'Historic aircraft on display',
  wayside_shrine: 'Roadside shrine or sacred marker',
  wayside_cross: 'Roadside Christian cross or shrine marker',
  boundary_stone: 'Historic marker showing a border or limit',
  manor: 'Historic palace, manor house, or chief\'s residence',
  ruins: 'Ruined historic structure still visible',
  tomb: 'Tomb, mausoleum, or burial shrine',

  // — Trees —
  'tree:mango': 'Mango tree — note variety in description if known',
  'tree:coconut': 'Coconut palm',
  'tree:oil_palm': 'Oil palm (Elaeis)',
  'tree:neem': 'Neem tree (Azadirachta indica)',
  'tree:mahogany': 'Mahogany or similar hardwood',
  'tree:baobab': 'Baobab (Adansonia)',
  'tree:shea': 'Shea nut tree (Vitellaria)',
  'tree:teak': 'Teak or plantation timber tree',
  'tree:other': 'Notable tree when species is unknown',
};

function hintKey(featureTypeId: string | undefined, categoryId: string): string {
  if (!featureTypeId) return categoryId;
  const scoped = `${featureTypeId}:${categoryId}`;
  if (CONTRIBUTION_CATEGORY_HINTS[scoped]) return scoped;
  return categoryId;
}

export function applyCategoryHints<T extends { id: string; hint?: string }>(
  categories: T[],
  featureTypeId?: string,
): T[] {
  return categories.map((c) => ({
    ...c,
    hint: c.hint ?? CONTRIBUTION_CATEGORY_HINTS[hintKey(featureTypeId, c.id)],
  }));
}

export function categorySearchText(
  category: { id: string; name: string; hint?: string },
): string {
  return [category.name, category.id, category.hint ?? ''].join(' ').toLowerCase();
}
